import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { IoMdClose } from "react-icons/io";
import { CategoryInfo } from "../../../../types/budget/import-services";
import { BudgetType, CalcOption } from "../../../../types/budget/budget";
import { TypePill, pillStyles } from "../ActiveBudget/ActiveBudget";

interface ImportServicesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImport: (
    mapping: Map<string, CalcOption>,
    mode: "services" | "categories_only",
    type: BudgetType
  ) => void;
  onCreateEmpty: (type: BudgetType) => void;
  categoriesWithServices: CategoryInfo[];
  hasEnabledCategories: boolean;
  /** Há linhas no Estudo de Verba, mesmo sem categoria. */
  hasDraftItems?: boolean;
}

const ImportServicesModal: React.FC<ImportServicesModalProps> = ({
  isOpen,
  onClose,
  onImport,
  onCreateEmpty,
  categoriesWithServices,
  hasEnabledCategories,
  hasDraftItems = false,
}) => {
  /**
   * O tipo vale para o orçamento inteiro e é definido só aqui, na criação — não
   * há default: sem escolha explícita a criação fica bloqueada.
   */
  const [budgetType, setBudgetType] = useState<BudgetType | null>(null);
  const [typeError, setTypeError] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setBudgetType(null);
      setTypeError(false);
    }
  }, [isOpen]);

  const handleTypeChange = (type: BudgetType) => {
    setBudgetType(type);
    setTypeError(false);
  };

  const handleImport = () => {
    if (!budgetType) {
      setTypeError(true);
      return;
    }
    if (budgetType === BudgetType.LOGISTICA) return;

    onImport(new Map(), "services", budgetType);
  };

  const handleCreateEmpty = () => {
    if (!budgetType) {
      setTypeError(true);
      return;
    }

    onCreateEmpty(budgetType);
  };

  const isCategoriesOnlyMode =
    categoriesWithServices.length === 0 && hasEnabledCategories;

  const canImportDraft =
    hasDraftItems || categoriesWithServices.length > 0 || hasEnabledCategories;

  const isImportDisabled =
    budgetType === BudgetType.LOGISTICA ||
    (budgetType !== null && !canImportDraft);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: "400px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1361A4" }}>
          Novo orçamento
        </Typography>
        <IconButton onClick={onClose} size="small">
          <IoMdClose />
        </IconButton>
      </DialogTitle>

      <Divider />

      <Box sx={{ px: 3, pt: 2.5, pb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
          Tipo do orçamento
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TypePill
            label="Logística"
            active={budgetType === BudgetType.LOGISTICA}
            activeStyle={pillStyles.activeLog}
            disabled={false}
            onClick={() => handleTypeChange(BudgetType.LOGISTICA)}
          />
          <TypePill
            label="Produção"
            active={budgetType === BudgetType.PRODUCAO}
            activeStyle={pillStyles.activePro}
            disabled={false}
            onClick={() => handleTypeChange(BudgetType.PRODUCAO)}
          />
        </Box>

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, color: "text.secondary" }}
        >
          O tipo se aplica a todo o orçamento. Para trabalhar com Logística e
          Produção, crie orçamentos separados.
        </Typography>

        {typeError && (
          <Typography
            variant="caption"
            role="alert"
            sx={{ display: "block", mt: 0.5, color: "#D32F2F" }}
          >
            O campo "Tipo do orçamento" é obrigatório
          </Typography>
        )}
      </Box>

      <Divider />

      <DialogContent sx={{ pt: 3 }}>
        {budgetType === BudgetType.LOGISTICA ? (
          <Typography variant="body2" color="text.secondary">
            O Estudo de Verba é exclusivo para Produção. Orçamentos de Logística
            são criados vazios, sem importar o rascunho.
          </Typography>
        ) : budgetType === BudgetType.PRODUCAO ? (
          <Typography variant="body2" color="text.secondary">
            Os itens do Estudo de Verba serão importados na ordem do rascunho,
            com a forma de pagamento já escolhida em cada linha.
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Escolha Logística ou Produção. O rascunho da aba Produção só entra
            em orçamentos de Produção.
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Box sx={{ flex: 1 }} />

        <Button
          onClick={onClose}
          variant="text"
          sx={{ textTransform: "none", color: "#555" }}
        >
          Cancelar
        </Button>

        <Button
          onClick={handleCreateEmpty}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderColor: "#D32F2F",
            color: "#D32F2F",
            "&:hover": {
              borderColor: "#B71C1C",
              bgcolor: "rgba(211, 47, 47, 0.04)",
            },
          }}
        >
          Não importar nada
        </Button>

        <Button
          onClick={handleImport}
          variant="contained"
          disabled={isImportDisabled}
          sx={{
            textTransform: "none",
            bgcolor: "#1361A4",
            "&:hover": {
              bgcolor: "#0D4A7A",
            },
          }}
        >
          {budgetType === BudgetType.PRODUCAO
            ? "Importar estudo de verba"
            : categoriesWithServices.length > 0
            ? "Importar serviços"
            : isCategoriesOnlyMode
            ? "Importar as categorias"
            : "Importar dados"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportServicesModal;
